'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_FABRIC_FEATURE,
  DELETE_FABRIC_FEATURE,
  GET_FABRIC_FEATURE,
  GET_FEATURE_GROUPS_FOR_SELECT,
  UPDATE_FABRIC_FEATURE,
} from '../fabricFeaturesQueries'
import { FabricFeatureFormValues } from '../types'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const formSchema = z.object({
  code: z.string().optional(),
  fabricFeatureGroupId: z.number().nullable().optional(),
  fabric_feature_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  image: z.array(z.string()).optional(),
}) satisfies z.ZodType<FabricFeatureFormValues>

interface FabricFeatureFormProps {
  data: {
    id: number | null
    defaultGroupId?: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function FabricFeatureForm({
  data,
  onDirtyChange,
  onRequestClose,
}: FabricFeatureFormProps) {
  const { allLanguages, languageId, language: currentLanguage } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueFabricFeature: featureData } = {},
    loading: loadingData,
  } = useQuery(GET_FABRIC_FEATURE, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const { data: groupsData } = useQuery(GET_FEATURE_GROUPS_FOR_SELECT, {
    fetchPolicy: 'cache-and-network',
  })

  const availableGroups = groupsData?.findManyFabricFeatureGroup || []

  const getGroupName = (group: any) => {
    return (
      group.fabric_feature_group_profiles.find(
        (p: any) => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      group.fabric_feature_group_profiles[0]?.name ||
      'Unknown Group'
    )
  }

  const form = useForm<FabricFeatureFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      fabricFeatureGroupId: undefined,
      fabric_feature_profiles: [],
      image: [],
    },
  })

  const [updateFabricFeature] = useMutation(UPDATE_FABRIC_FEATURE, {
    onError: () => {
      toast.error('Failed to update fabric feature')
    },
    onCompleted: () => {
      toast.success('Fabric feature updated successfully')
    },
    refetchQueries: ['GET_FABRIC_FEATURES', 'GetFabricFeature', 'GET_FABRIC_FEATURES_TREE'],
  })

  const [createFabricFeature] = useMutation(CREATE_FABRIC_FEATURE, {
    onError: () => {
      toast.error('Failed to create fabric feature')
    },
    onCompleted: response => {
      toast.success('Fabric feature created successfully')
      const newId = response.createFabricFeature.id
      setId(newId)
    },
    refetchQueries: ['GET_FABRIC_FEATURES', 'GET_FABRIC_FEATURES_TREE'],
  })

  const [deleteFabricFeature] = useMutation(DELETE_FABRIC_FEATURE, {
    refetchQueries: ['GET_FABRIC_FEATURES', 'GetFabricFeature', 'GET_FABRIC_FEATURES_TREE'],
  })

  const handleDelete = () => {
    if (id) {
      const count = featureData?._count?.fabric_groups ?? 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteFabricFeature({
              variables: { where: { id } },
            })
            toast.success('Fabric feature deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete fabric feature')
          }
        },
        description: count
          ? `This feature is used by ${count} fabric group(s). Deleting it will remove it from those groups. This action cannot be undone.`
          : 'Are you sure you want to delete this feature? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  function onSubmit(values: FabricFeatureFormValues) {
    if (id) {
      const updateData: any = {
        code: values.code,
        fabricFeatureGroupId: values.fabricFeatureGroupId ?? null,
        profiles: values.fabric_feature_profiles.map(p => ({
          id: p.id,
          language: p.language,
          name: p.name || '',
          description: p.description,
        })),
      }

      if (values.image && values.image.length > 0) {
        updateData.image = values.image[0]
      }

      updateFabricFeature({
        variables: {
          where: { id },
          data: updateData,
        },
      })
    } else {
      createFabricFeature({
        variables: {
          data: {
            code: values.code,
            fabricFeatureGroupId: values.fabricFeatureGroupId ?? null,
            image:
              values.image && values.image.length > 0
                ? values.image[0]
                : undefined,
            profiles: values.fabric_feature_profiles.map(p => ({
              language: p.language,
              name: p.name || '',
              description: p.description,
            })),
          },
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (featureData) {
        const existingProfiles =
          featureData.fabric_feature_profiles?.filter(
            (p: any) => p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: any) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        form.reset({
          code: featureData.code ?? '',
          fabricFeatureGroupId: featureData.fabricFeatureGroupId ?? null,
          fabric_feature_profiles: allProfiles,
          image: featureData.photo?.src ? [featureData.photo.src] : [],
        })
      } else {
        form.reset({
          code: '',
          fabricFeatureGroupId: data?.defaultGroupId ?? null,
          fabric_feature_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
          image: [],
        })
      }
    }
  }, [featureData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Fabric Feature</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <FormField
                control={form.control}
                name="fabricFeatureGroupId"
                render={({ field }) => (
                  <InputFieldContainer
                    label="Feature Group"
                    description="Select the group this feature belongs to."
                    control={form.control}
                    name="fabricFeatureGroupId"
                    render={() => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Select
                            onValueChange={val =>
                              field.onChange(val === 'none' ? null : Number(val))
                            }
                            value={
                              field.value == null
                                ? 'none'
                                : field.value.toString()
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a group" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No group</SelectItem>
                              {availableGroups.map((group: any) => (
                                <SelectItem
                                  key={group.id}
                                  value={group.id.toString()}
                                >
                                  {getGroupName(group)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              />

              <InputFieldContainer
                label="Feature Name"
                description="This is the public display name."
                control={form.control}
                name={`fabric_feature_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                description="Unique identifier code for this feature."
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="e.g. fabric-texture" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`fabric_feature_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Photo"
                description="Upload feature photo"
                control={form.control}
                name="image"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
